import { type TypedDocumentNode, gql } from '@apollo/client';
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import {
  PlayerFieldStatus,
  PlayerPlayingStatus,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { isType, withFragments } from '@sorare/core/src/lib/gql';

import { isGameScheduled } from 'shared/game/lib';

import type {
  isAnnouncedStarter_playerGameScore,
  isAnnouncedSubstitute_playerGameScore,
  isSubstitutedIn_playerGameScore,
  isSubstitutedOut_playerGameScore,
} from './__generated__/playingStatus.graphql';

const playingStatusMessages = defineMessages<PlayerPlayingStatus>({
  [PlayerPlayingStatus.STARTER]: {
    id: 'playingStatus.starter',
    defaultMessage: 'First team',
  },
  [PlayerPlayingStatus.REGULAR]: {
    id: 'playingStatus.injured',
    defaultMessage: 'Squad player',
  },
  [PlayerPlayingStatus.SUPER_SUBSTITUTE]: {
    id: 'playingStatus.superSubstitute',
    defaultMessage: 'Impact player',
  },
  [PlayerPlayingStatus.SUBSTITUTE]: {
    id: 'playingStatus.substitute',
    defaultMessage: 'Bench',
  },
  [PlayerPlayingStatus.NOT_PLAYING]: {
    id: 'playingStatus.reserve',
    defaultMessage: 'Not playing',
  },
  [PlayerPlayingStatus.RETIRED]: {
    id: 'playingStatus.retired',
    defaultMessage: 'Retired',
  },
});

export const formatPlayingStatus = (
  value: string,
  formatMessage: (message: MessageDescriptor) => string
) => {
  const playingStatus = value.toUpperCase() as PlayerPlayingStatus;
  if (playingStatus in playingStatusMessages)
    return formatMessage(playingStatusMessages[playingStatus]);
  return value;
};

const ORDERED_PLAYING_STATUS = [
  PlayerPlayingStatus.STARTER,
  PlayerPlayingStatus.REGULAR,
  PlayerPlayingStatus.SUPER_SUBSTITUTE,
  PlayerPlayingStatus.SUBSTITUTE,
  PlayerPlayingStatus.NOT_PLAYING,
  PlayerPlayingStatus.RETIRED,
];

export const sortPlayingStatus = (items: RefinementListItem[]) =>
  sortBy(
    item => {
      return (ORDERED_PLAYING_STATUS as unknown as string[]).indexOf(
        item.label.toUpperCase()
      );
    },
    [...items]
  );

export const isAnnouncedStarter = withFragments(
  (playerGameScore: Nullable<isAnnouncedStarter_playerGameScore>) => {
    return (
      playerGameScore?.scoreStatus === ScoreStatus.REVIEWING &&
      isType(playerGameScore.anyPlayerGameStats, 'PlayerGameStats') &&
      playerGameScore.anyPlayerGameStats?.fieldStatus ===
        PlayerFieldStatus.ON_FIELD &&
      isGameScheduled(playerGameScore.anyGame.status)
    );
  },
  {
    playerGameScore: gql`
      fragment isAnnouncedStarter_playerGameScore on PlayerGameScoreInterface {
        id
        scoreStatus
        anyGame {
          id
          status: statusTyped
        }
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            fieldStatus
          }
        }
      }
    ` as TypedDocumentNode<isAnnouncedStarter_playerGameScore>,
  }
);

export const isAnnouncedSubstitute = withFragments(
  (playerGameScore: isAnnouncedSubstitute_playerGameScore) => {
    return (
      playerGameScore?.scoreStatus === ScoreStatus.PENDING &&
      isType(playerGameScore.anyPlayerGameStats, 'PlayerGameStats') &&
      playerGameScore.anyPlayerGameStats?.fieldStatus ===
        PlayerFieldStatus.ON_BENCH &&
      isGameScheduled(playerGameScore.anyGame.status)
    );
  },
  {
    playerGameScore: gql`
      fragment isAnnouncedSubstitute_playerGameScore on PlayerGameScoreInterface {
        id
        scoreStatus
        anyGame {
          id
          status: statusTyped
        }
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            fieldStatus
          }
        }
      }
    ` as TypedDocumentNode<isAnnouncedSubstitute_playerGameScore>,
  }
);

export const isSubstitutedIn = withFragments(
  (playerGameScore: Nullable<isSubstitutedIn_playerGameScore>) => {
    return (
      isType(playerGameScore?.anyPlayerGameStats, 'PlayerGameStats') &&
      playerGameScore.anyPlayerGameStats.fieldStatus ===
        PlayerFieldStatus.ON_FIELD &&
      !playerGameScore.anyPlayerGameStats.gameStarted &&
      !isGameScheduled(playerGameScore.anyGame?.status)
    );
  },
  {
    playerGameScore: gql`
      fragment isSubstitutedIn_playerGameScore on PlayerGameScoreInterface {
        id
        anyGame {
          id
          status: statusTyped
        }
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            fieldStatus
            gameStarted
          }
        }
      }
    ` as TypedDocumentNode<isSubstitutedIn_playerGameScore>,
  }
);

export const isSubstitutedOut = withFragments(
  (playerGameScore: Nullable<isSubstitutedOut_playerGameScore>) => {
    return (
      isType(playerGameScore?.anyPlayerGameStats, 'PlayerGameStats') &&
      playerGameScore.anyPlayerGameStats?.fieldStatus ===
        PlayerFieldStatus.SUBSTITUTED &&
      !isGameScheduled(playerGameScore.anyGame?.status)
    );
  },
  {
    playerGameScore: gql`
      fragment isSubstitutedOut_playerGameScore on PlayerGameScoreInterface {
        id
        anyGame {
          id
          status: statusTyped
        }
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            fieldStatus
          }
        }
      }
    ` as TypedDocumentNode<isSubstitutedOut_playerGameScore>,
  }
);
